import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { DropdownBox } from "../../components/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPencil, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import useDoubleScroll from "../../helpers/useDoubleScroll";
import "../../Subscriber/subscribe.css";

const Salary = ({ userRole }) => {
  const [loading, setLoading] = useState(false);
  const [salaryMonth, setSalaryMonth] = useState(""); // selected month
  const [monthList, setMonthList] = useState([]);
  const [salaryReport, setSalaryReport] = useState([]);
  const [nameSearch, setNameSearch] = useState("");
  const [generated, setGenerated] = useState(0);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(""); // optional
  const [endDate, setEndDate] = useState(""); // optional
  const token = localStorage.getItem("authToken");

  const filteredSalaryReport = salaryReport.filter((row) => {
    if (!nameSearch.trim()) return true;
    const name = row.user_fullname || "";
    return name.toLowerCase().includes(nameSearch.toLowerCase().trim());
  });

  const tableContainerRef = useRef(null);
  const topScrollRef = useDoubleScroll(tableContainerRef, [salaryReport, filteredSalaryReport]);

  const managePage = "Salary Report";

  // Fetch month list and salary report
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch months
        const monthRes = await axios.get(`${API_URL}report/month-list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMonthList(monthRes.data.data || []);

        // Fetch salary report
        const reportRes = await axios.get(`${API_URL}report/salary-report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (reportRes.data.success) {
          setSalaryReport(reportRes.data.data || []);
          setGenerated(reportRes.data.generated || 0);
        }
      } catch (error) {
        console.error(error);
        showToast.error("Failed to load salary report");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Submit salary month filter
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}report/filter-salary`,
        { smonth: salaryMonth },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setSalaryReport(res.data.data);
        showToast.success("Report filtered");
      }
    } catch (error) {
      console.error(error);
      showToast.error("Failed to filter report");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSalaryMonth("");
    setSalaryReport([]);
    setNameSearch("");
  };

  const handleExport = (url) => {
    if (salaryMonth) {
      window.location.href = `${url}${salaryMonth}`;
    } else {
      window.location.href = url;
    }
  };

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in">
      {loading && <LoadingSpinner />}
      <div id="content" className="mt-5">
        {/* Breadcrumb */}
        <div className="row pb-2">
          <div className="col-md-6">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/">
                  <FontAwesomeIcon icon={faArrowUp} /> Home
                </a>
              </li>
              <li className="breadcrumb-item active">{managePage}</li>
            </ul>
          </div>

          <div className="col-md-6 text-right">
            {userRole?.canGenerate && (
              <button
                className="btn btn-default btn-sm mr-2"
                onClick={() => handleExport(`${API_URL}report/generateSalaryExport/`)}
              >
                Generate Report
              </button>
            )}
            {generated === 1 && (
              <>
                <button
                  className="btn btn-default btn-sm mr-2"
                  onClick={() => handleExport(`${API_URL}report/SalaryExport/`)}
                >
                  <FontAwesomeIcon icon={faDownload} /> Export Report
                </button>
                <button
                  className="btn btn-default btn-sm mr-2"
                  onClick={() => handleExport(`${API_URL}report/attendanceExport/`)}
                >
                  <FontAwesomeIcon icon={faDownload} /> Export Attendance
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filter Panel */}
        <div className="panel panel-default with-rounded-corner with-shadow mb-3">
          <form className="stdform" onSubmit={handleSubmit}>
            <div className="panel-heading">
              <span className="panel-title">{managePage}</span>
            </div>
            <div className="panel-body">
              <div className="row align-items-end">
                <div className="col-md-4">
                  <DropdownBox
                    label="Salary Month"
                    name="smonth"
                    options={monthList.map((m) => ({ value: m.id, label: m.name }))}
                    value={salaryMonth}
                    onChange={(e) => setSalaryMonth(e.target.value)}
                  />
                </div>
                <div className="col-md-auto mb-2">
                  <button type="submit" className="btn btn-primary">
                    Submit
                  </button>
                  <button type="button" className="btn btn-danger ms-2" onClick={handleClear}>
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Salary Table */}
        {salaryReport.length > 0 ? (
          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="large-table-container-3">
              <div
                ref={topScrollRef}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "2px",
                  background: "#f8f9fa",
                }}
              >
                <div style={{ width: "100%", height: "1px" }}></div>
              </div>
              <div
                ref={tableContainerRef}
                style={{
                  overflowY: "auto",
                  maxHeight: "calc(100vh - 250px)",
                  overflowX: "auto",
                  position: "relative",
                }}
              >
                <table className="table table-condensed table-hover bg-white sticky-table">
                  <thead>
                    <tr>
                      <th className="text-center sticky-col-1">SI. No.</th>
                      <th className="text-center sticky-col-2">Emp. Code</th>
                      <th className="text-start sticky-col-3" style={{ verticalAlign: "top" }}>
                        <div className="d-flex flex-column gap-1">
                          <span>Name</span>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search name..."
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: "11px",
                              height: "24px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              border: "1px solid #cbd5e1",
                              background: "#ffffff",
                              fontWeight: "normal",
                            }}
                          />
                        </div>
                      </th>
                      <th className="text-start">Designation</th>
                      <th className="text-center">Location</th>
                      <th className="text-center">UAN No.</th>
                      <th className="text-center">Total No. of Days</th>
                      <th className="text-center">No. of Worked Days</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Monthly Salary</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Basic</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Eligible Basic</th>
                      <th className="text-end" style={{ textAlign: "right" }}>HRA</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Conveance</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Medical</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Special Allow</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Other Allow</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Fixed Gross Salary</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Basic</th>
                      <th className="text-end" style={{ textAlign: "right" }}>HRA</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Conveance</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Medical</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Special Allow</th>
                      <th className="text-end" style={{ textAlign: "right" }}>FBP Benefit</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Incentive</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Revenue Target</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Revenue</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Shortage</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Sunday & Holiday Salary</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Earned Gross Salary</th>
                      <th className="text-end" style={{ textAlign: "right" }}>EFP</th>
                      <th className="text-end" style={{ textAlign: "right" }}>ESI Eligible</th>
                      <th className="text-end" style={{ textAlign: "right" }}>ESIC</th>
                      <th className="text-end" style={{ textAlign: "right" }}>PT</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Other Deduction</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Total Deduction</th>
                      <th className="text-end" style={{ textAlign: "right" }}>Net Salary</th>
                      {generated === 1 && <th className="text-center">Download</th>}
                      {userRole?.canEdit && <th className="text-center">Edit</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSalaryReport.length > 0 ? (
                      filteredSalaryReport.map((row, index) => (
                        <tr key={row.id}>
                          <td className="text-center sticky-col-1">{index + 1}</td>
                          <td className="text-center sticky-col-2" style={{ fontWeight: 600 }}>{row.user_code}</td>
                          <td className="sticky-col-3" style={{ fontWeight: 500, color: "#1e293b" }}>{row.user_fullname}</td>
                          <td>{row.user_designation}</td>
                          <td className="text-center">Bangalore</td>
                          <td className="text-center">{row.uan_number}</td>
                          <td className="text-center">{row.total_days || "-"}</td>
                          <td className="text-center">{row.working_days || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.monthly_salary || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_basic || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_eligible || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_hra || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_conveance || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_medical || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_special || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_allow || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.fixed_gross_salary || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_basic || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_hra || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_conveance || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_medical || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_special_allow || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_fbp_benefit || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_incentive || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.revenue_target || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.revenue || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.shortage || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.holiday_salary || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.earned_gross_salary || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.efp || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.esi_eligible || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.esic || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.pt || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.other_deduction || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right" }}>{row.total_deduction || "-"}</td>
                          <td className="text-end" style={{ textAlign: "right", fontWeight: 600, color: "#0f172a" }}>{row.net_salary || "-"}</td>
                          {generated === 1 && (
                            <td className="text-center">
                              <a href={`${API_URL}downloadPayslip/${row.id}`} target="_blank" rel="noreferrer" className="text-primary" title="Download Payslip">
                                <FontAwesomeIcon icon={faDownload} />
                              </a>
                            </td>
                          )}
                          {userRole?.canEdit && (
                            <td className="text-center">
                              <a href={`${API_URL}editPayslip/${row.id}`} className="text-primary" title="Edit Payslip">
                                <FontAwesomeIcon icon={faPencil} />
                              </a>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={38} className="text-center py-4 text-muted">
                          No employee matching &quot;{nameSearch}&quot;
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="panel panel-default with-rounded-corner with-shadow p-4 text-center text-muted">
            <p className="mb-0">No Records Found</p>
          </div>
        )}
        <style>{`
          .sticky-col-1 {
            position: sticky !important;
            left: 0px !important;
            width: 55px !important;
            min-width: 55px !important;
            max-width: 55px !important;
          }
          .sticky-col-2 {
            position: sticky !important;
            left: 55px !important;
            width: 105px !important;
            min-width: 105px !important;
            max-width: 105px !important;
          }
          .sticky-col-3 {
            position: sticky !important;
            left: 160px !important;
            width: 175px !important;
            min-width: 175px !important;
            box-shadow: 2px 0 5px rgba(0, 0, 0, 0.08) !important;
          }
          thead th.sticky-col-1,
          thead th.sticky-col-2,
          thead th.sticky-col-3 {
            z-index: 25 !important;
            background: #f8f9fa !important;
          }
          tbody td.sticky-col-1,
          tbody td.sticky-col-2,
          tbody td.sticky-col-3 {
            z-index: 5 !important;
            background: #ffffff !important;
          }
          .table-hover tbody tr:hover td.sticky-col-1,
          .table-hover tbody tr:hover td.sticky-col-2,
          .table-hover tbody tr:hover td.sticky-col-3 {
            background-color: #f1f5f9 !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Salary;
